import {
    DOMUtil, DynamicLoader, Head, InstanceIdentifier, LogManager, ServiceAddresses, setDefaultErrorHandler,
    SettingsLoader
} from "@mcleod/core";
import { Layout, MainPageAccessor, MainPageDef, Panel, PanelStaticError } from "@mcleod/components";
import { CommonDialogs } from "../CommonDialogs";
import { MainPageModule } from "./MainPageModule";
import { MainPageProps } from "./MainPageProps";
import { PageHeader } from "../pageheader/PageHeader";
import { Router } from "../router/Router";
import { UnsavedDataListener } from "../UnsavedDataListener";

const log = LogManager.getLogger("common.mainpage.MainPage");

export abstract class MainPage extends Panel implements MainPageProps, MainPageDef {
    private static _instance: MainPage
    apiContext: string;
    modules: MainPageModule[];
    onError: any;
    doNotReportNextError: boolean;

    public abstract get pageHeader(): PageHeader;
    public abstract get router(): Router;
    public abstract evaluateExternalLinkVisibility(topMostPanel: Panel, currentLayouts: Layout[]): void;

    protected constructor(props: Partial<MainPageProps>) {
        super({ padding: 0, fillRow: true, fillHeight: true, id: "main", ...props });
        this.setMainPageInstance();

        if (props?.apiContext != null) {
            ServiceAddresses.setContextPath(props.apiContext);
        }

        if (props == null) {
            throw new Error("MainPage requires a properties object to be passed in.");
        }

        if (props.modules == null) {
            throw new Error("MainPage requires an array of routable modules.");
        }

        for (const module of props.modules) {
            DynamicLoader.mapPathsToClasses(module.path, module.context);
        }
        DynamicLoader.mapPathsToClasses("components", require['context']("@mcleod/components/src", true));

        this._element.style.fontFamily = "Roboto, Helvetica, Arial, sans-serif";
        UnsavedDataListener.init();
        InstanceIdentifier._clear();

        setDefaultErrorHandler(error => {
            try {
                if (this.doNotReportNextError !== true)
                    CommonDialogs.showError(error);
            } finally {
                this.doNotReportNextError = false;
            }
        });

        this.searchUnauthSettings(props);
    }

    protected searchUnauthSettings(props: Partial<MainPageProps>) {
        SettingsLoader.loadUnauthSettings(
            this.beforeSettingsApply,
            this.afterSettingsApply,
            (error: any) => this.handleSettingsError(error, props))
    }

    private beforeSettingsApply = (response: any): boolean => {
        if (response.lucky_orange_site_id != null) {
            Head.addScript("https://tools.luckyorange.com/core/lo.js?site-id=" + response.lucky_orange_site_id);
        }
        return true;
    }

    private afterSettingsApply = (_response: any, _appliedSettings: boolean) => {
        this.add(this.router);
        this.performInitialRouting();
    }

    protected performInitialRouting() {
        this.router.redisplayCurrentRoute();
    }

    private handleSettingsError(error: any, props: Partial<MainPageProps>) {
        if (props.onError == null) {
            const errorSummary = "Sorry, we can't connect you to the server.";
            const errorDetail = "The server may not be running or you may have lost network connectivity.";
            this.add(new PanelStaticError(errorSummary, errorDetail));
        } else {
            props.onError(this, error);
        }
    }

    public static get mainLayout(): Layout {
        if (MainPage.instance == null) {
            return null;
        }

        let result = Router.instance.components[0];
        if (!(result instanceof Layout) && result?.["layout"] != null) {
            result = result["layout"];
        }
        if (result instanceof Layout) {
            return result;
        }
        return null;
    }

    private setMainPageInstance() {
        MainPage._instance = this;
        new MainPageAccessor(this);
    }

    public static get instance(): MainPage {
        return this._instance;
    }

    public get mainPage(): MainPage {
        return MainPage.instance;
    }

    public get mainPageHeight(): number {
        return DOMUtil.getComputedSize("height", MainPage.instance._element);
    }

    public get mainPageWidth(): number {
        return DOMUtil.getComputedSize("width", MainPage.instance._element);
    }

    public get routerHeight(): number {
        return DOMUtil.getComputedSize("height", MainPage.instance.router._element);
    }

    public get routerWidth(): number {
        return DOMUtil.getComputedSize("width", MainPage.instance.router._element);
    }

    public get pageHeaderHeight(): number {
        return DOMUtil.getComputedSize("height", MainPage.instance.pageHeader._element);
    }

    public get pageHeaderWidth(): number {
        return DOMUtil.getComputedSize("width", MainPage.instance.pageHeader._element);
    }

    overridePageHeaderZIndex(componentZIndex: number, millis: number): void {
        this.pageHeader.overridePageHeaderZIndex(componentZIndex, millis);
    }

    resetPageHeaderZIndex(): void {
        this.pageHeader.resetPageHeaderZIndex();
    }
}
